// client/src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import * as authApi from '../api/auth'; // Import your frontend auth API service

// Create the Auth Context
const AuthContext = createContext(null);

/**
 * Custom hook to use the AuthContext.
 * Provides easy access to authentication state and functions.
 */
export const useAuth = () => {
  return useContext(AuthContext);
};

/**
 * AuthProvider component that wraps your application.
 * Managess authentication state (user, token) and provides login/logout/register functions.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // To indicate if auth state is being loaded

  useEffect(() => {
    const loadUser = async () => {
      try {
        // Attempt to fetch current user data using the cookie (handled by browser)
        const userData = await authApi.fetchCurrentUser();
        // If userData is null (due to 401), it means the user is not authenticated.
        // Otherwise, set the user data.
        setUser(userData ? userData.user : null); 
      } catch (error) {
        console.error('Failed to fetch user on app load:', error);
        setUser(null);
      }
      setLoading(false); // Authentication check is complete
    };

    loadUser();
  }, []); // Run only once on mount

  // --- Authentication Functions ---

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await authApi.loginUser(email, password, rememberMe);
      setUser(response.user); // Store user data
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error; // Re-throw for UI to handle
    }
  };

  const register = async (userData) => {
    try {
      const response = await authApi.registerUser(userData);
      setUser(response.user); // Store user data
      return response;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logoutUser();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null); // Clear user state even if API call fails
    }
  };

  const updateOnboardingStatus = async (status) => {
    const originalUser = user;
    try {
      // Optimistically update the user state
      setUser({ ...user, onboardingStatus: status });
      await authApi.updateOnboardingStatus(status);
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
      // Revert to the original user state if the API call fails
      setUser(originalUser);
      throw error;
    }
  };

  const updateCurrentOnboardingPhase = async (phase) => {
    const originalUser = user;
    try {
      // Optimistically update the user state
      setUser({ ...user, currentOnboardingPhase: phase });
      await authApi.updateCurrentOnboardingPhase(phase);
    } catch (error) {
      console.error('Failed to update onboarding phase:', error);
      // Revert to the original user state if the API call fails
      setUser(originalUser);
      throw error;
    }
  };

  const setPhase1CompletionCelebrated = async () => {
    const originalUser = user;
    try {
      // Optimistically update the user state
      setUser({ ...user, phase1_completion_celebrated: true });
      await authApi.setPhase1CompletionCelebrated();
    } catch (error) {
      console.error('Failed to mark Phase 1 celebration as shown:', error);
      // Revert to the original user state if the API call fails
      setUser(originalUser);
      throw error;
    }
  };

  // The value provided to consumers of this context
  const authContextValue = {
    user,
    isAuthenticated: !!user, // Convenience boolean
    loading,
    login,
    register,
    logout,
    setUser, // Allows direct update of user object (e.g., after profile update)
    updateOnboardingStatus, // Expose the new function
    updateCurrentOnboardingPhase, // Expose phase tracking function
    setPhase1CompletionCelebrated, // Expose Phase 1 celebration flag function
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};